import React from "react";
import {Alert, Form} from "react-bootstrap";
import {PiWarningCircle} from "react-icons/pi";
import {ReactSelectBootstrap} from "react-select-bootstrap";
import DatePickerField from "./DatePickerField.jsx";
import HelpTooltip from "../HelpTooltip.jsx";
import {formatDateDE, spansBookingCutover} from "./format.js";

const PRESETS = [
    {id: "last12", label: "Letzte 12 Monate"},
    {id: "last24", label: "Letzte 24 Monate"},
    {id: "ytd", label: "Laufendes Jahr"},
    {id: "prevYear", label: "Vorjahr"},
    {id: "all", label: "Gesamter Zeitraum"},
];

export const LEVELS = [
    {id: "article", label: "Je Artikel"},
    {id: "parent", label: "Je Übernummer"},
];

/**
 * Umschalter "Auswertungsebene" - gilt für alle drei Reiter der
 * Verkaufsstatistik gemeinsam (siehe Statistics/Index.jsx) und steht
 * deshalb als eigene, wiederverwendbare Komponente hier: Auf dem
 * Dashboard/Detail-Reiter erscheint er als Teil dieser Filterleiste, auf
 * dem Lagerstrategie-Reiter (der keine FilterBar zeigt, siehe dort) wird
 * er isoliert oberhalb der Seite eingebunden - beide Stellen greifen auf
 * denselben Code zurück, damit die Bedienung identisch bleibt.
 */
export function LevelToggle({level, onLevelChange}) {
    return (
        <div className="d-inline-flex align-items-stretch p-1 rounded-pill bg-body-secondary flex-wrap" role="group" aria-label="Auswertungsebene">
            {LEVELS.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    aria-pressed={level === item.id}
                    className={`btn btn-sm rounded-pill border-0 ${level === item.id ? "bg-white text-body shadow-sm fw-semibold" : "text-body"}`}
                    style={{padding: ".3rem .9rem"}}
                    onClick={() => onLevelChange(item.id)}
                >
                    {item.label}
                </button>
            ))}
        </div>
    );
}

/**
 * Warnt davor, einen Zeitraum über die Buchungsumstellung 2020/2021 hinweg
 * auf Artikel-Ebene auszuwerten (siehe spansBookingCutover in format.js für
 * die fachliche Begründung - bis 2020 wurde auf die Übernummer gebucht, ab
 * 2021 auf die Einzelfarbe). Erscheint nur, wenn beide Bedingungen
 * zutreffen, und bietet direkt die Schaltfläche zum Umschalten an, statt
 * nur zu warnen.
 */
export function BookingCutoverHint({dateFrom, dateTo, level, onLevelChange}) {
    if (level !== "article" || !spansBookingCutover(dateFrom, dateTo)) {
        return null;
    }
    return (
        <Alert variant="warning" className="d-flex align-items-start gap-2 mt-2 mb-0 py-2">
            <PiWarningCircle size={20} className="flex-shrink-0 mt-1" aria-hidden="true" />
            <div className="small d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                <span>
                    Der gewählte Zeitraum reicht über die Umstellung der Buchungsweise (2020/2021) hinweg. Auf
                    Artikel-Ebene sind die Zahlen dadurch verzerrt - bis 2020 wurde auf die Übernummer gebucht, ab
                    2021 auf die Einzelfarbe. Für Vergleiche über diesen Zeitpunkt hinaus die Ebene „Je Übernummer"
                    verwenden.
                </span>
                <button
                    type="button"
                    className="btn btn-sm btn-warning text-nowrap flex-shrink-0"
                    onClick={() => onLevelChange("parent")}
                >
                    Auf „Je Übernummer" umschalten
                </button>
            </div>
        </Alert>
    );
}

/**
 * Gemeinsame Filterleiste über allen Auswertungen: Zeitraum-Schnellwahl mit
 * frei wählbarem Von/Bis, Auswertungsebene, Warengruppen-Mehrfachauswahl und
 * Suche. Der Zustand liegt bewusst bei der aufrufenden Seite
 * (Statistics/Index.jsx), damit exakt dieselben Filter an Kennzahlen,
 * Diagramme und Detailtabelle gehen.
 */
// Klarnamen der Datenquellen. In der Tabelle stehen technische Kürzel; in
// der Oberfläche soll lesbar sein, aus welchem System ein Zeitabschnitt
// stammt.
const QUELLEN_NAMEN = {
    majesty: "Majesty",
    weclapp: "Weclapp",
};

export default function FilterBar({
    preset,
    onPresetSelect,
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange,
    dateMin,
    dateMax,
    sourceRanges,
    level,
    onLevelChange,
    groupOptions,
    articleGroups,
    onArticleGroupsChange,
    search,
    onSearchChange,
}) {
    const selectedGroupOptions = groupOptions.filter((option) => articleGroups.includes(option.value));

    return (
        <div className="mb-3">
        <div className="d-flex flex-wrap align-items-end gap-3">
            <div className="d-inline-flex align-items-stretch p-1 rounded-pill bg-body-secondary flex-wrap" role="group" aria-label="Zeitraum-Schnellwahl">
                {PRESETS.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        aria-pressed={preset === item.id}
                        className={`btn btn-sm rounded-pill border-0 ${preset === item.id ? "bg-white text-body shadow-sm fw-semibold" : "text-body"}`}
                        style={{padding: ".3rem .9rem"}}
                        onClick={() => onPresetSelect(item.id)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            <div className="d-flex align-items-end gap-2">
                <div>
                    <Form.Label className="small text-muted mb-1" htmlFor="statistics-date-from">Von</Form.Label>
                    <DatePickerField
                        id="statistics-date-from"
                        label="Zeitraum von"
                        size="sm"
                        value={dateFrom}
                        min={dateMin}
                        max={dateTo || dateMax}
                        onChange={onDateFromChange}
                        style={{width: 148}}
                    />
                </div>
                <div>
                    <Form.Label className="small text-muted mb-1" htmlFor="statistics-date-to">Bis</Form.Label>
                    <DatePickerField
                        id="statistics-date-to"
                        label="Zeitraum bis"
                        size="sm"
                        value={dateTo}
                        min={dateFrom || dateMin}
                        max={dateMax}
                        onChange={onDateToChange}
                        style={{width: 148}}
                    />
                </div>
            </div>

            <div>
                <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                    Auswertungsebene
                    <HelpTooltip placement="right" size={0.7}>
                        Bis 2020 wurden Verkäufe auf die Übernummer (Sammelartikel über allen Farbvarianten) gebucht,
                        ab 2021 auf die Einzelfarbe. „Je Übernummer" fasst beide Buchungsweisen zusammen und ist die
                        richtige Ebene für Vergleiche über diesen Zeitpunkt hinaus.
                    </HelpTooltip>
                </Form.Label>
                <LevelToggle level={level} onLevelChange={onLevelChange} />
            </div>

            <div style={{minWidth: 260, flex: "1 1 260px", maxWidth: 420}}>
                <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                    Warengruppen
                    <HelpTooltip placement="right" size={0.7}>
                        Mehrfachauswahl. Ohne Auswahl gelten alle Warengruppen.
                    </HelpTooltip>
                </Form.Label>
                <ReactSelectBootstrap
                    isMulti
                    placeholder="Alle Warengruppen"
                    noOptionsMessage={() => "Keine Warengruppe gefunden"}
                    options={groupOptions}
                    value={selectedGroupOptions}
                    onChange={(value) => onArticleGroupsChange((value || []).map((item) => item.value))}
                />
            </div>

            <div style={{minWidth: 220, flex: "1 1 220px", maxWidth: 320}}>
                <Form.Label className="small text-muted mb-1" htmlFor="statistics-search">Suche</Form.Label>
                <Form.Control
                    id="statistics-search"
                    type="search"
                    placeholder="Artikelnummer oder Bezeichnung…"
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                />
            </div>
        </div>
        {dateMax && (
            <div className="small text-muted mt-1">
                Datenbestand bis {formatDateDE(dateMax)}
                {/* Seit Juli 2026 speist sich die Statistik aus zwei Systemen.
                    Welcher Abschnitt woher stammt, gehört sichtbar dazu -
                    beide Systeme haben unterschiedlich gebucht. */}
                {Array.isArray(sourceRanges) && sourceRanges.length > 0 && (
                    <>
                        {" – "}
                        {sourceRanges
                            .map((bereich) => `${QUELLEN_NAMEN[bereich.source] || bereich.source}: ${formatDateDE(bereich.min)} bis ${formatDateDE(bereich.max)}`)
                            .join(" · ")}
                    </>
                )}
            </div>
        )}
        <BookingCutoverHint dateFrom={dateFrom} dateTo={dateTo} level={level} onLevelChange={onLevelChange} />
        </div>
    );
}
